import Rioglyph from '@rio-cloud/rio-uikit/Rioglyph';
import { uniq } from 'es-toolkit';
import { FormattedMessage } from 'react-intl';
import { BulkStatusError, BulkStatusErrorMessage } from './BulkStatusErrorMessage';
import { ReactNode } from 'react';

type BulkUpdateValidationFeedbackProps<TErrorCode extends string> = {
    readonly isSuccess: boolean;
    readonly validItemsCount: number;
    readonly invalidItems: readonly BulkStatusError<TErrorCode>[];
    readonly getErrorMessageId: (errorCode: TErrorCode) => string;
    readonly requestError?: ReactNode;
};

export const BulkUpdateValidationFeedback = <TErrorCode extends string>({
    isSuccess,
    requestError,
    validItemsCount,
    invalidItems,
    getErrorMessageId,
}: BulkUpdateValidationFeedbackProps<TErrorCode>) => {
    const uniqueInvalidItemCount = uniq(invalidItems.flatMap((error) => error.identifiers)).length;
    const totalItemsCount = validItemsCount + uniqueInvalidItemCount;

    let feedback;
    if (requestError) {
        feedback = requestError;
    } else if (isSuccess && validItemsCount > 0) {
        feedback = (
            <>
                <div>
                    <FormattedMessage
                        id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.successCount"
                        values={{ successCount: validItemsCount, totalCount: totalItemsCount }}
                    />
                </div>
                {invalidItems.length > 0 && (
                    <BulkStatusErrorMessage errors={invalidItems} getErrorMessageId={getErrorMessageId} />
                )}
            </>
        );
    } else {
        feedback = (
            <>
                <div>
                    <FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.failure" />
                </div>
                {invalidItems.length > 0 && (
                    <BulkStatusErrorMessage errors={invalidItems} getErrorMessageId={getErrorMessageId} />
                )}
            </>
        );
    }

    const iconConfig = isSuccess
        ? { icon: 'rioglyph-check-badge', iconClassName: 'text-color-success' }
        : { icon: 'rioglyph-remove-sign', iconClassName: 'text-color-danger' };

    return (
        <div className="min-height-300 display-flex flex-column justify-content-center align-items-center gap-20">
            <div className="text-size-16">
                <FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.title" />
            </div>
            <div>
                <Rioglyph icon={iconConfig.icon} iconClassName={iconConfig.iconClassName} size="h1" />
            </div>
            {feedback}
        </div>
    );
};
